import { CaptureUpdateAction, newElementWith, restoreElements } from "@excalidraw/excalidraw";
import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";
import type { ToolContext } from "./types";

const ELEMENT_TYPES = new Set([
  "rectangle",
  "diamond",
  "ellipse",
  "text",
  "arrow",
  "line",
  "freedraw",
  "image",
  "frame",
  "magicframe",
  "embeddable",
  "iframe",
]);
const GEOMETRY_KEYS = ["x", "y", "width", "height", "angle"] as const;

export function mutationError(ctx: ToolContext): string | null {
  if (!ctx.excalidrawAPI) return "No Excalidraw editor is currently active";
  if (ctx.isReadOnly !== false || ctx.excalidrawAPI.getAppState().viewModeEnabled) {
    return "This canvas is read-only. WebMCP cannot edit it.";
  }
  return null;
}

/** The only WebMCP scene-write boundary; persistence stays with EditorPage.onChange. */
export function commitCanvas(
  ctx: ToolContext,
  candidate: readonly Record<string, unknown>[],
  validate?: (elements: readonly ExcalidrawElement[]) => void,
): void {
  const denied = mutationError(ctx);
  if (denied) throw new Error(denied);
  const api = ctx.excalidrawAPI!;
  const previous = api.getSceneElementsIncludingDeleted();
  const previousById = new Map(previous.map((element) => [element.id, element]));
  const candidateById = new Map(candidate.map((element) => [element.id, element]));
  const ids = new Set<string>();

  for (const element of candidate) {
    if (!element || typeof element !== "object" || Array.isArray(element)) {
      throw new Error("Each element must be an object");
    }
    if (typeof element.id !== "string" || !element.id || ids.has(element.id)) {
      throw new Error("Each element must have a unique non-empty ID");
    }
    ids.add(element.id);
    if (typeof element.type !== "string" || !ELEMENT_TYPES.has(element.type)) {
      throw new Error("Unsupported element type");
    }
    for (const key of GEOMETRY_KEYS) {
      if (
        element[key] !== undefined &&
        (typeof element[key] !== "number" || !Number.isFinite(element[key]))
      ) {
        throw new Error(`Element ${key} must be a finite number`);
      }
    }
    if ((element.width as number) < 0 || (element.height as number) < 0) {
      throw new Error("Element dimensions must not be negative");
    }
  }

  // getSceneElements() omits tombstones. Keep them for collaboration reconciliation.
  const complete = [
    ...candidate,
    ...previous.filter((element) => element.isDeleted && !ids.has(element.id)),
  ];
  const restored = restoreElements(complete as ExcalidrawElement[], previous, {
    refreshDimensions: true,
    repairBindings: true,
  });
  if (restored.length !== complete.length) {
    throw new Error("One or more elements could not be restored");
  }
  const elements = restored.map((element) => {
    const old = previousById.get(element.id);
    if (!old) return element;
    // An untouched input must not be rewritten just because restore supplies defaults.
    if (candidateById.get(old.id) === old || (!candidateById.has(old.id) && old.isDeleted))
      return old;
    if (old.type !== element.type) throw new Error("An element's type cannot be changed");
    const next = {
      ...element,
      boundElements:
        !element.boundElements?.length && !old.boundElements?.length
          ? old.boundElements
          : element.boundElements,
      version: old.version,
      versionNonce: old.versionNonce,
      updated: old.updated,
    };
    const unchanged = Object.entries(next).every(
      ([key, value]) =>
        JSON.stringify(value) === JSON.stringify(old[key as keyof ExcalidrawElement]),
    );
    return unchanged ? old : newElementWith(old, next, true);
  });
  validate?.(elements);
  if (elements.length === previous.length && elements.every((el, i) => el === previous[i])) return;
  api.updateScene({ elements, captureUpdate: CaptureUpdateAction.IMMEDIATELY });
}
